import type { OnlineStorePublishSummary } from "../shopify_publish/types";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

export interface DCDesign {
    id?: number;
    brand_id: number;
    title: string;
    pattern_type?: string | null;
    collection_id?: number | null;
    pricing_template_id?: number | null;
    description_template_id?: number | null;
    status?: 'in_progress_0' | 'in_progress_1' | 'in_progress_2' | 'in_progress_3' | 'in_progress_3.1' | 'in_progress_3.2' | 'in_progress_3.3' | 'in_progress_4.1' | 'in_progress_4.2' | 'in_progress_5' | 'in_progress_5.1' | 'in_progress_5.2' | 'in_progress_5.3' | 'in_progress_5.4' | 'in_progress_5.5' | 'in_progress_5.6' | 'approved' | 'needs_changes' | 'rejected' | 'staged' | 'released';
    current_version_id?: number | null;
    shopify_product_id?: string | null;
    listing_images_disabled?: boolean;
    /**
     * Per-design opt-in for publishing a second, wrapping-paper Shopify listing on
     * Release. The brand-level `wrapping_paper` feature flag decides whether the
     * option is offered at all; this records whether THIS design wants it.
     */
    infinity_mural_mode?: 'responsive' | 'crop' | null;
    current_version?: DCDesignVersion | null;
    // Catalog licensing — present only once a licensing price has been set.
    licensing?: DCDesignLicensing | null;
    // Server-derived: true once licensing exists on the design.
    is_catalog_design?: boolean;
    // True once a released catalog design is actually visible to other brands.
    catalog_published?: boolean;
    created_at?: string;
    updated_at?: string;
}

// #region DCVariantLinkCandidate
// Server-side Link Variant search result. Designs already in another variant group
// come back with linkable=false and a human-readable reason.
export interface DCVariantLinkCandidate {
    design: DCDesign;
    linkable: boolean;
    reason: string | null;
    group_name: string | null;
}

export interface DCVariantLinkCandidateResult {
    candidates: DCVariantLinkCandidate[];
    has_more: boolean;
}

// Exactly one licensing model may be active on a design at a time.
export type DCLicensingModel = 'PERCENTAGE' | 'FLAT';
export type DCLicensingInterval = 'MONTHLY' | 'YEARLY';

export interface DCDesignLicensing {
    id: string;
    design_id: string;
    model: DCLicensingModel;
    percentage_value: number | null;
    flat_amount: number | null;
    flat_interval: DCLicensingInterval | null;
    currency: string;
}

// XOR payload: percentage_value OR (flat_amount + flat_interval), never both.
export interface DCDesignLicensingInput {
    model: DCLicensingModel;
    percentage_value?: number;
    flat_amount?: number;
    flat_interval?: DCLicensingInterval;
    currency?: string;
}

// #region BulkLicensing
/** One design that did NOT get published in a bulk run. Reasons are stable
 *  strings so the UI can group identical ones ("5 designs: 'reason'"). */
export interface DCBulkLicensingOutcome {
    design_id: number;
    design_title: string | null;
    reason: string;
}

/** Summary of a bulk licensing run. Partial failure is normal, not an error. */
export interface DCBulkLicensingResult {
    total: number;
    priced: number;
    published: number;
    skipped: DCBulkLicensingOutcome[];
    failed: DCBulkLicensingOutcome[];
}

/** Where a design sits in the licensing funnel. */
export type DCLicensingStatusFilter = 'NOT_LICENSED' | 'PRICED_NOT_PUBLISHED' | 'IN_CATALOG';

export interface DCDesignVersion {
    id?: number;
    design_id: number;
    version_label: string;
    image_url: string;
    preview_url?: string | null;
    dpi?: number | null;
    width_px?: number | null;
    height_px?: number | null;
    width_in?: number | null;
    height_in?: number | null;
    notes?: string | null;
    created_by?: number | null;
    created_at?: string;
    // Brand's chosen fill strategy when the artwork is under 16in and the 16x16 swatch
    // must be squared. Null = the backend derives it from pattern_type.
    swatch_fill_mode?: DCSwatchFillMode | null;
    // Computed server-side (not a column) — true when a dimension is under 16in and we
    // should prompt the brand to pick a fill mode.
    needs_swatch_fill_choice?: boolean;
}

// How a non-square design is squared up into the 16" x 16" swatch.
export type DCSwatchFillMode = 'tile' | 'upscale';

export type DCDesignCreateInput = {
    brand_id: number;
    title: string;
    pattern_type?: string | null;
    collection_id?: number | null;
    status?: DCDesign["status"];
    image_url: string;
    version_label?: string;
    dpi?: number | null;
    width_px?: number | null;
    height_px?: number | null;
    width_in?: number | null;
    height_in?: number | null;
    notes?: string | null;
    created_by?: number | null;
};

export interface DCDesignCollection {
    id?: number;
    brand_id?: number | null;
    name: string;
    is_artist_based?: boolean;
    is_global?: boolean;
    square_logo_url?: string | null;
    rectangular_logo_url?: string | null;
    /** S3 key of the uploaded custom collection header. */
    header_image_url?: string | null;
    /** RPE path written back once the header rip completes. */
    header_image_filepath?: string | null;
    header_rip_status?: 'unprocessed' | 'processing' | 'dropped' | 'completed' | null;
    created_at?: string;
    updated_at?: string;
}

export interface DCDesignComment {
    id?: number;
    design_id: number;
    user_id?: number | null;
    user?: {
        first?: string | null;
        last?: string | null;
    } | null;
    body: string;
    type?: 'manual' | 'automated';
    needs_attention?: boolean;
    attention_resolved?: boolean;
    attention_resolved_at?: string | null;
    attention_resolved_by?: number | null;
    attention_resolved_by_user?: {
        first?: string | null;
        last?: string | null;
    } | null;
    event_data?: any[] | null;
    created_at?: string;
}

export interface DCDesignColor {
    id?: number;
    design_id: number;
    name: string;
    hex: string;
    is_primary?: boolean;
    coverage_pct?: number;
    created_at?: string;
}

export interface DCDesignInsight {
    id?: number;
    design_id: number;
    popularity_score: number;
    popularity_reason?: string | null;
    target_audiences?: any;
    recommended_rooms?: any;
    style_tags?: any;
    theme_tags?: any;
    mood_tags?: any;
    medium_tags?: any;
    misc_tags?: any;
    color_tags?: any;
    room_tags?: any;
    dominant_category?: string | null;
    alt_categories?: any;
    alternative_names?: any;
    wallpaper_description?: string | null;
    fabric_description?: string | null;
    wrapping_paper_description?: string | null;
    marketing_description?: string | null;
    created_at?: string;
}

export interface DCDesignRelease {
    id?: number;
    design_id: number;
    /**
     * Response-only (release call): whether the products created by this
     * release reached the Online Store. Absent on re-releases and polls.
     */
    online_store_publish?: OnlineStorePublishSummary | null;

    status: 'pending' | 'processing' | 'completed' | 'failed';
    design_peel_rpe?: string | null;
    design_luxe_rpe?: string | null;
    design_wrap_rpe?: string | null;
    sample_peel_rpe?: string | null;
    sample_luxe_rpe?: string | null;
    completion_percentage?: number;
    released_by?: number | null;
    release_snapshot?: Record<string, any>;
    pending_changes?: Record<string, any>;
    last_synced_at?: string | null;
    proof_only?: boolean;
    proof_panel_images?: string[] | null;
    proof_validated?: boolean;
    /**
     * Why the wrapping-paper listing failed to publish on the most recent release,
     * or null. A giftwrap failure deliberately does not fail the release — the
     * wallpaper listing is never rolled back over it — so this is the only place the
     * user learns the second product is missing.
     */
    giftwrap_error?: string | null;
    created_at?: string;
    updated_at?: string;
}

/** A published Shopify listing for a design. One row per product type. */
export interface DCDesignListing {
    id?: number;
    design_id: number;
    product_type: ListingProductType;
    shopify_product_id?: string | null;
    short_product_id?: string | null;
    status?: string | null;
    created_at?: string;
    updated_at?: string;
}

export interface DCReleaseRequirement {
    label: string;
    met: boolean;
    description?: string;
    optional?: boolean;
}

export interface DCReleaseRequirementsResponse {
    requirements: DCReleaseRequirement[];
    all_met: boolean;
}

export interface StepChange {
    changed: boolean;
    requires_rpe: boolean;
    current?: any;
    released?: any;
}

export interface ChangeDetectionResult {
    has_changes: boolean;
    release: DCDesignRelease;
    changes: {
        step_1_1?: StepChange;
        step_2_1?: StepChange;
        step_3_1?: StepChange;
        step_3_2?: StepChange;
        step_3_3?: StepChange;
        step_4_1?: StepChange;
        step_5_1?: StepChange;
        step_5_2?: StepChange;
        [key: string]: StepChange | undefined;
    };
    requires_rpe_generation: boolean;
    can_update: boolean;
}

export interface UpdateListingRequest {
    steps: string[];
}

export interface UpdateListingResponse {
    success: boolean;
    updated_steps: string[];
    failed_steps: string[];
    errors?: Record<string, string>;
    release: DCDesignRelease;
}

export interface DCDesignSample {
    id?: number;
    design_id: number;
    status?: 'pending' | 'generating' | 'completed' | 'failed';
    coordinates?: any;
    wallpaper_scale_in?: number | null;
    sample_primary_path?: string | null;
    sample_rotated_path?: string | null;
    sample_listing_image?: string | null;
    is_selected?: boolean | null;
    created_at?: string;
    updated_at?: string;
}

/**
 * Which Shopify listing a listing image belongs to. A design publishes one product
 * per product type, each with its own independently-ordered gallery.
 */
export type ListingProductType = 'wallpaper' | 'giftwrap' | 'fabric';

/**
 * Mockup category ids from `order.mockup_category` where category_type='PRODUCT_TYPE'.
 * These are database ids, not an app-side enum — they must match the rows exactly.
 */
export const MOCKUP_PRODUCT_TYPE = {
    GIFTWRAP: 49,
    WALLPAPER: 50,
    FABRIC: 51,
} as const;

export interface DCDesignMockup {
    id?: number;
    design_id: number;
    mockup_template_id?: number | null;
    /**
     * Resolved from the source template's `mockups.product_type` — see
     * MOCKUP_PRODUCT_TYPE. This is what lets the wallpaper and wrapping-paper
     * listing-image steps show different candidate pools. Null on manual uploads,
     * which have no template and are therefore offered to both.
     */
    product_type?: number | null;
    status?: 'pending' | 'generating' | 'completed' | 'failed';
    thumbnail_path?: string | null;
    mockup_path?: string | null;
    status_reason?: string | null;
    manual_upload?: boolean;
    hidden?: boolean;
    shadow_opacity?: number;
    enhance_shadows?: boolean;
    created_at?: string;
    updated_at?: string;
    // Soft-delete columns surface through the admin recovery endpoint only. Regular
    // GraphQL reads automatically exclude soft-deleted rows server-side, so these are
    // optional and present only when the row was fetched via the admin path.
    deleted_at?: string | null;
    deleted_by?: number | null;
    delete_reason?: string | null;
}

export interface DCDesignListingImage {
    id?: number;
    design_id: number;
    product_type?: ListingProductType;
    source_type: 'mockup' | 'sample' | 'shopify';
    source_id: number;
    source_url?: string | null;
    position: number;
    alt_text?: string | null;
    seo_filename?: string | null;
    seo_completed_at?: string | null;
    created_at?: string;
    updated_at?: string;
}

export interface DCDesignScaleOption {
    id?: number;
    design_id: number;
    scale_value: number;
    width_inches: number;
    height_inches: number;
    estimated_dpi: number;
    requires_tiling: boolean | "1" | "0";
    is_default_wallpaper: boolean;
    is_default_wrapping_paper: boolean;
    is_default_fabric: boolean;
    // "Supported" scales per product type; the default row is always enabled.
    is_enabled_wallpaper?: boolean;
    is_enabled_wrapping_paper?: boolean;
    is_enabled_fabric?: boolean;
    is_disabled_wallpaper?: boolean;
    is_disabled_wrapping_paper?: boolean;
    is_disabled_fabric?: boolean;
    is_minimum_wallpaper?: boolean;
    created_at?: string;
    updated_at?: string;
}

// Mirrors order-handler/src/data/interfaces/design_wall_tile.ts
export interface DCDesignWallTile {
    id: number;
    design_id: number;
    design_version_id?: number | null;
    scale_option_id: number;
    scale_value: number;
    scale_width_inches: number;
    scale_height_inches: number;
    wall_width_ft: number;
    wall_height_ft: number;
    is_custom_wall?: boolean;
    repeats_x: number;
    repeats_y: number;
    output_width_px: number;
    output_height_px: number;
    image_path: string;
    image_url?: string | null;
    file_name: string;
    created_at?: string;
    updated_at?: string;
}

// #region VariantGroupTypes
// Mirrors order-handler/src/data/interfaces/design_variant_group.ts
export interface DCDesignVariantGroup {
    id?: number;
    brand_id: number;
    name: string;
    primary_design_id?: number | null;
    shopify_parent_product_id?: string | null;
    created_at?: string;
    updated_at?: string;
    members?: DCDesignVariantGroupMember[];
    /** Members whose Shopify title push failed after a rename/link (dashboard titles still saved). */
    title_push_failures?: DCTitlePushFailure[];
}

export interface DCTitlePushFailure {
    design_id: number;
    title: string | null;
    message: string;
}

export interface DCDesignVariantGroupMember {
    id?: number;
    group_id: number;
    design_id: number;
    color_label?: string | null;
    is_primary?: boolean;
    shopify_child_product_id?: string | null;
    created_at?: string;
}

// Combined-listing readiness breakdown (mirrors order-handler CombinedListingReadiness).
// Surfaces each requirement independently so the eligibility modal can explain what's missing.
export interface CombinedListingReadiness {
    plusActive: boolean;
    planName: string | null;
    combinedListingsConfirmed: boolean;
    canConnect: boolean;
}

export const dcDesignsAtom = atom<DCDesign[]>([]);
export const dcVersionsAtom = atom<DCDesignVersion[]>([]);
export const dcCommentsAtom = atom<DCDesignComment[]>([]);
export const dcColorsAtom = atom<DCDesignColor[]>([]);
export const dcInsightAtom = atom<DCDesignInsight | null>(null);
export const dcLoadingAtom = atom<boolean>(false);

// #region PaginationTypes
export interface DCDesignFilters {
    status: string[];
    pattern_type: string[];
    released: 'RELEASED' | 'NOT_RELEASED' | undefined;
    title_search: string | undefined;
    default_wallpaper_scale: number[];
    needs_attention: boolean;
    created_by: number[];
    /** Licensing funnel filter — only used by the Licensing Setup page. */
    licensing_status?: DCLicensingStatusFilter[];
}

export interface DCDesignSort {
    column: 'created_at' | 'title' | 'status';
    direction: 'asc' | 'desc';
}

export interface DCDesignCreatorUser {
    id: number;
    first: string | null;
    last: string | null;
}

export interface DCDesignPaginatedNode {
    id: number;
    brand_id: number;
    title: string;
    status: string;
    pattern_type: string | null;
    collection_id: number | null;
    pricing_template_id: number | null;
    description_template_id: number | null;
    current_version_id: number | null;
    shopify_product_id: string | null;
    /** Needed to seed a draft line item's mural sizing when ordering from a paginated list. */
    infinity_mural_mode?: string | null;
    created_at: string;
    updated_at: string;
    current_version: DCDesignVersion | null;
    collection: DCDesignCollection | null;
    release: DCDesignRelease | null;
    has_unresolved_attention?: boolean;
    /** True when a default wallpaper scale option exists for this design. */
    has_default_wallpaper_scale?: boolean;
    /** True when a sample exists that is selected and finished rendering. */
    has_selected_sample?: boolean;
    /**
     * False when the design has not finished the setup wizard, so it cannot be
     * ordered as a print or a sample. Undefined on stale payloads — treat as true.
     */
    is_setup_complete?: boolean;
    created_by: number | null;
    created_by_user: DCDesignCreatorUser | null;
    insight: { id: number } | null;
    /**
     * True when another brand owns this design and this brand only licenses it.
     * Only populated on lists that request licensed designs (the order picker).
     */
    is_licensed?: boolean;
    /** Owning brand's name for licensed designs; null when this brand owns it. */
    owner_brand_name?: string | null;
    /** Licensing price is set (catalog opt-in). */
    is_catalog_design?: boolean;
    /** Design is live in the licensing catalog. */
    catalog_published?: boolean;
    /** Current licensing terms, when priced. */
    licensing?: DCDesignLicensing | null;
    /** True for a non-primary color-variant member: pricing is shared from the
     *  primary, so licensing cannot be set here (or in bulk). */
    is_variant_member_readonly?: boolean | null;
}

// A design is still being analyzed when it has no insight and is at the initial status.
// Used across all design list views to render a skeleton card instead of a clickable design.
export const isDesignAnalyzing = (design: DCDesignPaginatedNode): boolean =>
    design.status === 'in_progress_0' && !design.insight;

export interface DCPaginationState {
    cursor: string | null;
    hasMore: boolean;
    designs: DCDesignPaginatedNode[];
}

// #region PaginationAtoms
/**
 * Design-list state is SCOPED. Two lists can be on screen (or alive in memory) at
 * once — the design studio list and the order flow's design picker — and they must
 * not share filters, sort, cursor or accumulated pages. Filtering in one used to
 * silently refilter the other, because these were module-level singletons.
 *
 * Every consumer passes a scope; `useDesignCreator()` defaults to the studio scope
 * so existing call sites are unaffected.
 */
export type DesignListScope = string;

export const DESIGN_LIST_SCOPE_STUDIO: DesignListScope = 'studio';
export const DESIGN_LIST_SCOPE_ORDER_PICKER: DesignListScope = 'order-picker';
/** Bulk Licensing Setup page — its own filter/sort/pagination so it never fights the studio list. */
export const DESIGN_LIST_SCOPE_LICENSING: DesignListScope = 'licensing';

export const emptyDesignFilters = (): DCDesignFilters => ({
    status: [],
    pattern_type: [],
    released: undefined,
    title_search: undefined,
    default_wallpaper_scale: [],
    needs_attention: false,
    created_by: [],
    licensing_status: [],
});

export const emptyDesignSort = (): DCDesignSort => ({
    column: 'created_at',
    direction: 'desc',
});

export const emptyDesignPagination = (): DCPaginationState => ({
    cursor: null,
    hasMore: true,
    designs: [],
});

/* The scope parameter is the family KEY — atomFamily requires it in the signature
   even though these three initializers don't read it. */
/* eslint-disable @typescript-eslint/no-unused-vars */
export const dcFilterAtomFamily = atomFamily((_scope: DesignListScope) => atom<DCDesignFilters>(emptyDesignFilters()));
export const dcSortAtomFamily = atomFamily((_scope: DesignListScope) => atom<DCDesignSort>(emptyDesignSort()));
export const dcPaginationAtomFamily = atomFamily((_scope: DesignListScope) => atom<DCPaginationState>(emptyDesignPagination()));
/* eslint-enable @typescript-eslint/no-unused-vars */

/** The studio's instances, kept as named exports so existing imports keep working. */
export const dcFilterAtom = dcFilterAtomFamily(DESIGN_LIST_SCOPE_STUDIO);
export const dcSortAtom = dcSortAtomFamily(DESIGN_LIST_SCOPE_STUDIO);
export const dcPaginationAtom = dcPaginationAtomFamily(DESIGN_LIST_SCOPE_STUDIO);

// #region DescriptionTemplateTypes
export type DCDescriptionWidgetType =
    | 'custom_html'
    | 'color_palette'
    | 'description_text'
    | 'image'
    | 'ordered_list'
    | 'unordered_list';

export interface DCDescriptionTemplateWidget {
    id?: number;
    template_id: number;
    widget_type: DCDescriptionWidgetType;
    row_index: number;
    col_index: number;
    config: Record<string, any>;
    created_at?: string;
    updated_at?: string;
}

export interface DCDescriptionTemplate {
    id?: number;
    brand_id: number;
    name: string;
    is_default?: boolean;
    widgets?: DCDescriptionTemplateWidget[];
    created_at?: string;
    updated_at?: string;
}

export const dcDescriptionTemplatesAtom = atom<DCDescriptionTemplate[]>([]);

export const dcDesignsGroupedByCollectionAtomFamily = atomFamily((scope: DesignListScope) => atom((get) => {
    const { designs } = get(dcPaginationAtomFamily(scope));
    const grouped: Record<string, DCDesignPaginatedNode[]> = {};
    for (const design of designs) {
        const key = design.collection_id?.toString() ?? 'uncategorized';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(design);
    }
    return grouped;
}));

export const dcDesignsGroupedByCollectionAtom = dcDesignsGroupedByCollectionAtomFamily(DESIGN_LIST_SCOPE_STUDIO);

// #region DesignListViewMode
export type DesignListViewMode = 'grid' | 'table' | 'status' | 'collection';

const DESIGN_LIST_VIEW_MODE_KEY = 'design_list_view_mode';

function getStoredViewMode(): DesignListViewMode {
    try {
        const stored = localStorage.getItem(DESIGN_LIST_VIEW_MODE_KEY);
        if (stored === 'grid' || stored === 'table' || stored === 'status' || stored === 'collection') return stored;
    } catch { /* SSR / private browsing */ }
    return 'grid';
}

export const dcViewModeAtom = atom<DesignListViewMode>(getStoredViewMode());

// Writeable atom that also persists to localStorage
export const dcViewModeWriteAtom = atom(
    (get) => get(dcViewModeAtom),
    (_get, set, newMode: DesignListViewMode) => {
        set(dcViewModeAtom, newMode);
        try { localStorage.setItem(DESIGN_LIST_VIEW_MODE_KEY, newMode); } catch { /* ignore */ }
    }
);

// #region Grouped by Status
// Status workflow order for grouping
export const STATUS_GROUP_ORDER: string[] = [
    'in_progress_0',
    'in_progress_1',
    'in_progress_2',
    'in_progress_3',
    'in_progress_3.1',
    'in_progress_3.2',
    'in_progress_3.3',
    'in_progress_4.1',
    'in_progress_4.2',
    'in_progress_5',
    'in_progress_5.1',
    'in_progress_5.2',
    'in_progress_5.3',
    'in_progress_5.4',
    'in_progress_5.5',
    'in_progress_5.6',
    'approved',
    'needs_changes',
    'rejected',
    'staged',
    'released',
];

export const STATUS_LABELS: Record<string, string> = {
    in_progress_0: "New Design",
    in_progress_1: "Review & Name",
    in_progress_2: "Colors & Style",
    in_progress_3: "Product Setup",
    "in_progress_3.1": "Product Setup",
    "in_progress_3.2": "Product Setup",
    "in_progress_3.3": "Product Setup",
    "in_progress_4.1": "Generate Photos",
    "in_progress_4.2": "Generate Photos",
    in_progress_5: "Listing Setup",
    "in_progress_5.1": "Listing Setup",
    "in_progress_5.2": "Listing Setup",
    // One status per Listing Setup sub-step: 5.1 Pricing, 5.2 Listing Images,
    // 5.3 Wrap Listing Images, 5.4 Image SEO, 5.5 Digital Proof, 5.6 Release.
    "in_progress_5.3": "Listing Setup",
    "in_progress_5.4": "Listing Setup",
    "in_progress_5.5": "Ready For Release",
    "in_progress_5.6": "Ready For Release",
    approved: "Approved",
    needs_changes: "Changes",
    rejected: "Rejected",
    staged: "Staged",
    released: "Released",
};

export const dcDesignsGroupedByStatusAtomFamily = atomFamily((scope: DesignListScope) => atom((get) => {
    const { designs } = get(dcPaginationAtomFamily(scope));
    const grouped: Record<string, DCDesignPaginatedNode[]> = {};
    for (const design of designs) {
        const key = design.status ?? 'unknown';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(design);
    }
    return grouped;
}));

export const dcDesignsGroupedByStatusAtom = dcDesignsGroupedByStatusAtomFamily(DESIGN_LIST_SCOPE_STUDIO);


// #region isReadyForReleaseStatus
/** Digital Proof or Release — the "Ready For Release" stage of the wizard. */
export const isReadyForReleaseStatus = (status?: string | null): boolean =>
    status === 'in_progress_5.5' || status === 'in_progress_5.6';

// #region LISTING_SUBSTEP_STATUS
/**
 * The status saved while a designer is ON each Listing Setup sub-step. Fixed
 * per sub-step (it matches the wizard's numbering when every sub-step is
 * shown), so a value means the same step for every design.
 */
export const LISTING_SUBSTEP_STATUS = {
    pricing: 'in_progress_5.1',
    listing_images: 'in_progress_5.2',
    wrap_listing_images: 'in_progress_5.3',
    seo: 'in_progress_5.4',
    digital_proof: 'in_progress_5.5',
    release: 'in_progress_5.6',
} as const;
